/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* --- volume discount nudge --- */
/* ---------------------------------------------------------------------------
 * Lumirow · Multi-piece volume discount nudge
 *
 * 1. A progress bar inside the Zalify cart drawer and cart page.
 * 2. A single reminder popup after N seconds of active browsing since the
 *    last add-to-cart.
 *
 * Everything is driven by the theme's own events, so no polling is needed:
 *   variant:added                      -> a piece was just added (timer start)
 *   cart:updated                       -> cart content changed (bar refresh)
 *   cart:refresh                       -> cart section re-rendered
 *   line-item-quantity:change:start    -> cart is busy, do not interrupt
 *   line-item-quantity:change:end      -> cart is idle again
 *
 * No discount is ever announced as "unlocked" unless Shopify has actually
 * applied it to the cart (cart_level_discount_applications).
 * ------------------------------------------------------------------------ */

(function () {
  'use strict';

  var TICK = 1000;
  var STORE = 'vd:';

  var DEFAULTS = {
    title1: 'Your Next Piece Comes with a Little Extra',
    body1: 'Add one more piece to enjoy 10% OFF your jewelry.',
    title2: 'A Little More to Love',
    body2: 'Your 10% OFF is unlocked. Add one more piece to enjoy 15% OFF.',
    title3: "You're One Piece Away",
    body3: 'Add one more piece to unlock our best offer — 20% OFF.',
    codeText: 'BlessU',
    codeHeading: 'A Gift of Luck',
    codeNote:
      'A mystery surprise is tucked inside — paste it at checkout to reveal your gift. May the luck stay with you. ✨'
  };

  var cfg = null;
  var lastCart = null;
  var lastCount = -1;
  var lastTotal = 0;
  var lastTier = 0;
  var sessionId = null;
  var timerId = null;
  var popupEl = null;
  var persistCounter = 0;

  var state = {
    remaining: null,
    shown: false,
    busy: false,
    milestones: {}
  };

  function store(key, value) {
    try {
      if (value === undefined) return sessionStorage.getItem(STORE + key);
      sessionStorage.setItem(STORE + key, value);
    } catch (e) {
      /* Safari private mode */
    }
    return null;
  }

  function readConfig() {
    var el = document.querySelector('.vd-config');
    if (!el) return null;
    var d = el.dataset;
    var num = function (v, fallback) {
      var n = parseInt(v, 10);
      return isNaN(n) ? fallback : n;
    };
    return {
      enableBar: d.enableBar !== 'false',
      enablePopup: d.enablePopup !== 'false',
      mobile: d.mobile !== 'false',
      delay: num(d.delay, 60),
      accent: d.accent || '#FEBE98',
      continueUrl: d.continueUrl || '/collections/all',
      t1: { q: num(d.t1q, 2), p: num(d.t1p, 10) },
      t2: { q: num(d.t2q, 3), p: num(d.t2p, 15) },
      t3: { q: num(d.t3q, 4), p: num(d.t3p, 20) },
      total: num(d.total, 0),
      title1: d.title1 || DEFAULTS.title1,
      body1: d.body1 || DEFAULTS.body1,
      title2: d.title2 || DEFAULTS.title2,
      body2: d.body2 || DEFAULTS.body2,
      title3: d.title3 || DEFAULTS.title3,
      body3: d.body3 || DEFAULTS.body3,
      enableCode: d.code !== 'false',
      codeText: d.codeText || DEFAULTS.codeText,
      codeHeading: d.codeHeading || DEFAULTS.codeHeading,
      codeNote: d.codeNote || DEFAULTS.codeNote
    };
  }

  /* ------------------------------------------------------------------ cart */

  function itemCount(cart) {
    if (!cart) return 0;
    if (typeof cart.item_count === 'number') return cart.item_count;
    if (!cart.items) return 0;
    return cart.items.reduce(function (sum, i) {
      return sum + (i.quantity || 0);
    }, 0);
  }

  function appliedDiscount(cart) {
    var apps =
      (cart && (cart.cart_level_discount_applications || cart.cartLevelDiscountApplications)) ||
      [];
    var best = 0;
    var amount = 0;
    apps.forEach(function (a) {
      var p = 0;
      if (a.title && a.title.indexOf(cfg.t3.p + '%') > -1) p = cfg.t3.p;
      else if (a.title && a.title.indexOf(cfg.t2.p + '%') > -1) p = cfg.t2.p;
      else if (a.title && a.title.indexOf(cfg.t1.p + '%') > -1) p = cfg.t1.p;
      if (p > best) {
        best = p;
        amount = a.total_allocated_amount || a.totalAllocatedAmount || 0;
      }
    });
    return { pct: best, amount: amount };
  }

  function money(cents) {
    var fmt = '${{amount}}';
    try {
      fmt =
        (window.themeVariables &&
          window.themeVariables.settings &&
          window.themeVariables.settings.moneyFormat) ||
        '${{amount}}';
    } catch (e) {
      /* keep default */
    }
    return fmt.replace(/\{\{[^}]*\}\}/g, (cents / 100).toFixed(2));
  }

  function tierState(count) {
    if (count >= cfg.t3.q) return { reached: cfg.t3, next: null };
    if (count >= cfg.t2.q) return { reached: cfg.t2, next: cfg.t3 };
    if (count >= cfg.t1.q) return { reached: cfg.t1, next: cfg.t2 };
    return { reached: null, next: cfg.t1 };
  }

  function barCopy(count, appliedPct) {
    var s = tierState(count);
    var main = '';
    var sub = '';

    if (!s.reached) {
      var need = s.next.q - count;
      main = 'Add ' + need + ' more piece' + (need === 1 ? '' : 's') + ' to unlock ' + s.next.p + '% OFF!';
      sub = 'Black Friday pricing: more pieces, more savings.';
    } else if (!s.next) {
      main = appliedPct >= s.reached.p
        ? "You've unlocked our best offer — " + s.reached.p + '% OFF!'
        : "You're eligible for our best offer — " + s.reached.p + '% OFF.';
      sub = 'Your multi-piece savings are ready.';
    } else {
      main = appliedPct >= s.reached.p
        ? "You've unlocked " + s.reached.p + '% OFF!'
        : "You're eligible for " + s.reached.p + '% OFF.';
      var gap = s.next.q - count;
      sub = 'Add ' + gap + ' more piece' + (gap === 1 ? '' : 's') + ' to unlock ' + s.next.p + '% OFF.';
    }

    return { main: main, sub: sub };
  }

  /* Progress runs on three EQUAL segments, one per milestone, so the fill
     always lands exactly on a dot when a tier is reached:
       0 .. t1q  ->  0%   .. 33.3%
       t1q.. t2q -> 33.3% .. 66.7%
       t2q.. t3q -> 66.7% .. 100%
       >= t3q    -> 100%                                              */
  function progressPct(count) {
    var third = 100 / 3;
    if (count >= cfg.t3.q) return 100;
    if (count >= cfg.t2.q) {
      return third * 2 + (third * (count - cfg.t2.q)) / (cfg.t3.q - cfg.t2.q);
    }
    if (count >= cfg.t1.q) {
      return third + (third * (count - cfg.t1.q)) / (cfg.t2.q - cfg.t1.q);
    }
    return (third * count) / cfg.t1.q;
  }

  function paint(el, count, appliedPct, amount) {
    /* From now on this element is owned by JavaScript */
    el.removeAttribute('data-vd-server');

    var copy = barCopy(count, appliedPct);
    var pos = function (i) {
      return (i * 100) / 3;
    };
    var node = function (i, q, p) {
      return (
        '<span class="vd-bar__node ' +
        (count >= q ? 'is-reached' : '') +
        '" style="left: ' +
        pos(i) +
        '%;"><span class="vd-bar__dot"></span><span class="vd-bar__label">' +
        p +
        '%</span></span>'
      );
    };

    el.style.setProperty('--vd-accent', cfg.accent);
    el.style.setProperty('--vd-progress', progressPct(count) / 100);

    el.innerHTML =
      '<p class="vd-bar__main">' + copy.main + '</p>' +
      '<p class="vd-bar__sub">' + copy.sub + '</p>' +
      '<div class="vd-bar__track" role="progressbar" aria-label="Multi-piece discount progress" ' +
      'aria-valuemin="0" aria-valuemax="' + cfg.t3.q + '" aria-valuenow="' + count + '">' +
      '<span class="vd-bar__fill"></span>' +
      node(1, cfg.t1.q, cfg.t1.p) +
      node(2, cfg.t2.q, cfg.t2.p) +
      node(3, cfg.t3.q, cfg.t3.p) +
      '</div>' +
      (appliedPct > 0 && amount > 0
        ? '<p class="vd-bar__savings">Volume Discount (' +
          appliedPct +
          '%): &minus;' +
          money(amount) +
          '</p>'
        : '');
  }

  function refreshBars() {
    if (!cfg) return;
    var els = document.querySelectorAll('volume-discount-bar');
    var count = itemCount(lastCart);
    var applied = appliedDiscount(lastCart);

    Array.prototype.forEach.call(els, function (el) {
      if (!cfg.enableBar || count <= 0) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      paint(el, count, applied.pct, applied.amount);
    });

    lastTotal = lastCart ? lastCart.total_price || lastCart.totalPrice || 0 : 0;

    /* A hidden bar should never be left on screen */
    if (!cfg.enableBar) return;

    var tier = tierState(count);
    var tierPct = tier.reached ? tier.reached.p : 0;
    lastTier = tierPct;

    if (count > 0 && count !== lastCount) {
      if (lastCount === -1) track('volume_progress_bar_viewed', {});

      if (tier.reached) {
        var key = 'q' + tier.reached.q;
        var fired = store('milestones');
        fired = fired ? JSON.parse(fired) : {};
        if (!state.milestones[key] && !fired[key]) {
          state.milestones[key] = true;
          fired[key] = true;
          store('milestones', JSON.stringify(fired));
          track('volume_discount_milestone_reached', {
            tier_qty: tier.reached.q,
            tier_pct: tier.reached.p
          });
        }
      }
      lastCount = count;
    }
  }

  /* -------------------------------------------------------------- analytics */

  function track(name, extra) {
    var payload = {
      cart_count: itemCount(lastCart),
      cart_total: (lastTotal / 100).toFixed(2),
      current_tier: lastTier,
      device_type: window.matchMedia('(max-width: 740px)').matches ? 'mobile' : 'desktop',
      page_type: (window.themeVariables && window.themeVariables.settings.pageType) || '',
      session_id: sessionId
    };
    Object.keys(extra || {}).forEach(function (k) {
      payload[k] = extra[k];
    });

    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      var dl = { event: name };
      Object.keys(payload).forEach(function (k) {
        dl[k] = payload[k];
      });
      window.dataLayer.push(dl);
    }

    document.documentElement.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail: payload })
    );
  }

  /* ----------------------------------------------------------------- popup */

  function isMobile() {
    return window.matchMedia('(max-width: 740px)').matches;
  }

  function otherOverlayOpen() {
    var selectors = [
      '#mini-cart[open]',
      '#search-drawer[open]',
      '#newsletter-popup[open]',
      'modal-content[open]',
      '.modal[open]',
      'drawer-content[open]'
    ];
    for (var i = 0; i < selectors.length; i++) {
      if (document.querySelector(selectors[i])) return true;
    }
    return false;
  }

  function isBlocked() {
    if (!cfg || !cfg.enablePopup) return true;
    if (state.shown) return true;
    if (document.hidden) return true;
    if (window.location.pathname.indexOf('/checkout') === 0) return true;
    if (state.busy) return true;
    if (isMobile() && !cfg.mobile) return true;
    if (otherOverlayOpen()) return true;
    return false;
  }

  function popupCopy(count) {
    if (count <= 1) {
      return {
        title: cfg.title1,
        body: cfg.body1,
        primary: 'Continue Shopping',
        secondary: 'View Cart',
        secondaryAction: 'cart'
      };
    }
    if (count === 2) {
      return {
        title: cfg.title2,
        body: cfg.body2,
        primary: 'Explore More Pieces',
        secondary: 'Checkout',
        secondaryAction: 'checkout'
      };
    }
    return {
      title: cfg.title3,
      body: cfg.body3,
      primary: 'Continue Shopping',
      secondary: 'Checkout',
      secondaryAction: 'checkout'
    };
  }

  function closePopup(reason) {
    if (!popupEl) return;
    popupEl.removeAttribute('open');
    var el = popupEl;
    popupEl = null;
    window.setTimeout(function () {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    }, 250);
    if (reason === 'dismiss') track('volume_reminder_popup_dismissed', {});
  }

  /* Clipboard API needs a secure context; fall back to a hidden textarea. */
  function copyText(text) {
    var ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (e) {
      ok = false;
    }
    if (!ok) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch (e) {
        /* Last resort: the code is on screen, the shopper can type it */
      }
      document.body.removeChild(ta);
    }
    return ok;
  }

  function copyCode(btn, count) {
    if (btn.dataset.vdBusy === '1') return;
    btn.dataset.vdBusy = '1';

    copyText(cfg.codeText);
    track('volume_reminder_popup_code_copied', {
      cart_count: count,
      code: cfg.codeText
    });

    var hint = btn.querySelector('.vd-popup__code-hint');
    var previous = hint ? hint.textContent : '';
    btn.classList.add('is-copied');
    if (hint) hint.textContent = 'Now paste it at checkout ✨';
    window.setTimeout(function () {
      btn.classList.remove('is-copied');
      if (hint) hint.textContent = previous;
      delete btn.dataset.vdBusy;
    }, 2600);
  }

  function showPopup() {
    var count = itemCount(lastCart);
    if (count <= 0 || count >= cfg.t3.q) return;

    var copy = popupCopy(count);
    state.shown = true;
    store('shown', '1');

    /* The lucky-charm code: a dashed "coupon" the shopper taps to copy. */
    var coupon = '';
    if (cfg.enableCode) {
      coupon =
        '<div class="vd-popup__coupon">' +
        '<p class="vd-popup__coupon-heading">' +
        cfg.codeHeading +
        '</p>' +
        '<button type="button" class="vd-popup__code" data-vd="code" ' +
        'aria-label="Copy discount code ' +
        cfg.codeText +
        '">' +
        '<span class="vd-popup__code-value">' +
        cfg.codeText +
        '</span>' +
        '<span class="vd-popup__code-hint">Tap to copy</span>' +
        '</button>' +
        '<p class="vd-popup__coupon-note">' +
        cfg.codeNote +
        '</p>' +
        '</div>';
    }

    var wrap = document.createElement('div');
    wrap.className = 'vd-popup';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-label', copy.title);
    wrap.innerHTML =
      '<div class="vd-popup__card">' +
      '<button type="button" class="vd-popup__close" data-vd="close" aria-label="Close">&times;</button>' +
      '<p class="vd-popup__title">' + copy.title + '</p>' +
      '<p class="vd-popup__body">' + copy.body + '</p>' +
      coupon +
      '<div class="vd-popup__actions">' +
      '<button type="button" class="vd-popup__btn vd-popup__btn--primary" data-vd="primary">' +
      copy.primary +
      '</button>' +
      '<button type="button" class="vd-popup__btn vd-popup__btn--secondary" data-vd="secondary">' +
      copy.secondary +
      '</button>' +
      '</div>' +
      '</div>';

    document.body.appendChild(wrap);
    popupEl = wrap;

    requestAnimationFrame(function () {
      wrap.setAttribute('open', '');
      var closeBtn = wrap.querySelector('.vd-popup__close');
      if (closeBtn) closeBtn.focus();
    });

    wrap.addEventListener('click', function (event) {
      if (event.target === wrap) {
        closePopup('dismiss');
        return;
      }
      var btn = event.target.closest('[data-vd]');
      if (!btn) return;
      var kind = btn.getAttribute('data-vd');

      /* Copying the code must never close the popup */
      if (kind === 'code') {
        copyCode(btn, count);
        return;
      }

      /* The X button: explicit dismissal */
      if (kind === 'close') {
        closePopup('dismiss');
        return;
      }

      if (kind === 'primary') {
        track('volume_reminder_popup_primary_clicked', { cart_count: count });
        closePopup();
        window.location.href = cfg.continueUrl;
      } else {
        if (copy.secondaryAction === 'checkout') {
          track('volume_reminder_popup_checkout_clicked', { cart_count: count });
          closePopup();
          window.location.href =
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root
              : '/') + 'checkout';
        } else {
          track('volume_reminder_popup_dismissed', { action: 'view_cart' });
          closePopup();
          var drawer = document.getElementById('mini-cart');
          if (drawer && typeof drawer.open !== 'undefined') {
            drawer.open = true;
          } else {
            window.location.href =
              (window.themeVariables && window.themeVariables.routes.cartUrl) || '/cart';
          }
        }
      }
    });

    document.addEventListener('keydown', onPopupKeydown);
    track('volume_reminder_popup_shown', { cart_count: count });
  }

  function onPopupKeydown(event) {
    if (!popupEl) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      closePopup('dismiss');
      return;
    }
    if (event.key !== 'Tab') return;
    var focusable = popupEl.querySelectorAll('button, [href]');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ----------------------------------------------------------------- timer */

  function persistTimer() {
    if (state.remaining === null) {
      store('timer', JSON.stringify({ r: null, a: 0 }));
      return;
    }
    store('timer', JSON.stringify({ r: state.remaining, a: Date.now() }));
  }

  function startTimer() {
    var count = itemCount(lastCart);
    if (count <= 0) {
      state.remaining = null;
      persistTimer();
      return;
    }
    state.remaining = cfg.delay * 1000;
    persistTimer();
  }

  function tick() {
    if (!cfg || !cfg.enablePopup) return;
    if (state.remaining === null) return;
    if (isBlocked()) return;

    state.remaining -= TICK;
    if (state.remaining <= 0) {
      state.remaining = 0;
      persistTimer();
      if (itemCount(lastCart) > 0 && itemCount(lastCart) < cfg.t3.q) {
        showPopup();
      } else {
        state.remaining = null;
      }
      return;
    }

    persistCounter += 1;
    if (persistCounter % 5 === 0) persistTimer();
  }

  /* ------------------------------------------------------------------ boot */

  function onCartUpdated(event) {
    var previousCount = lastCount;

    var cart = event && event.detail && event.detail.cart;
    if (cart) lastCart = cart;
    refreshBars();

    var count = itemCount(lastCart);

    if (count <= 0) {
      /* Cart emptied: nothing left to remind anyone about. */
      state.remaining = null;
      persistTimer();
      return;
    }

    /* Only an ADD arms the reminder. Removing pieces, or simply re-rendering
       the cart, must never restart the countdown. */
    if (count > previousCount) startTimer();
  }

  function init() {
    cfg = readConfig();
    if (!cfg) return;

    sessionId = store('sid');
    if (!sessionId) {
      sessionId = 's' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      store('sid', sessionId);
    }

    state.shown = store('shown') === '1';

    var raw = store('timer');
    if (raw) {
      try {
        var o = JSON.parse(raw);
        state.remaining = typeof o.r === 'number' ? o.r : null;
      } catch (e) {
        state.remaining = null;
      }
    }

    /* Seed from what Liquid already knows, so the first "cart:updated" can
       tell the difference between "just added" and "nothing changed". */
    var initialCount =
      (window.themeVariables && window.themeVariables.settings.cartCount) || 0;
    if (initialCount > 0) {
      lastCart = { item_count: initialCount, items: [] };
      lastCount = initialCount;
      lastTotal = cfg.total;
      lastTier = tierState(initialCount).reached ? tierState(initialCount).reached.p : 0;
    }

    document.documentElement.addEventListener('cart:updated', onCartUpdated);

    /* "cart:refresh" carries the same cart payload (it is also fired on
       pageshow, i.e. when the shopper comes back to a restored tab). The
       markup is about to be replaced by the server, so we only sync state. */
    document.documentElement.addEventListener('cart:refresh', function (event) {
      var cart = event && event.detail && event.detail.cart;
      if (cart) lastCart = cart;
    });

    /* A real add-to-cart is the only thing that arms the timer. This matters
       when the theme is set to "cart page": the shopper is redirected, so the
       cart:updated event never arrives and only this hook survives. */
    document.documentElement.addEventListener('variant:added', function () {
      window.setTimeout(function () {
        if (state.remaining === null && itemCount(lastCart) > 0) startTimer();
      }, 0);
    });

    document.documentElement.addEventListener('line-item-quantity:change:start', function () {
      state.busy = true;
    });
    document.documentElement.addEventListener('line-item-quantity:change:end', function () {
      state.busy = false;
    });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) persistTimer();
    });

    document.addEventListener('click', function (event) {
      var t = event.target;
      if (t && t.closest && t.closest('[name="checkout"], [href*="/checkout"]')) {
        state.remaining = null;
        persistTimer();
      }
    });

    window.addEventListener('pagehide', persistTimer);

    if (cfg.enableBar) {
      if (!window.customElements.get('volume-discount-bar')) {
        window.customElements.define(
          'volume-discount-bar',
          class VolumeDiscountBar extends HTMLElement {
            connectedCallback() {
              /* A server-rendered bar is already correct: leave it alone until
                 the cart actually changes. */
              if (this.dataset.vdServer === '1') return;
              if (cfg && lastCart) {
                var applied = appliedDiscount(lastCart);
                if (!this.hidden) paint(this, itemCount(lastCart), applied.pct, applied.amount);
              }
            }
          }
        );
      }
      /* Deliberately no refreshBars() here: the Liquid snippet is the source of
         truth for the first paint, so we never blank a correct bar. */
      if (document.querySelector('volume-discount-bar')) {
        track('volume_progress_bar_viewed', {});
      }
    }

    timerId = window.setInterval(tick, TICK);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --- recently viewed products (mobile navigation drawer) --- */
(function () {
  var KEY = 'lumirow:recently-viewed';
  var MAX_STORED = 10;
  var VALIDATE_TTL = 5 * 60 * 1000;
  var lastValidate = 0;

  function readCfg() {
    var el = document.querySelector('.rv-config');
    if (!el) return null;
    var d = el.dataset;
    return {
      limit: parseInt(d.limit, 10) || 6,
      title: d.title || 'Recently Viewed',
      welcome: d.welcome || 'Welcome to Lumirow',
      ctaText: d.ctaText || 'Shop All',
      ctaUrl: d.ctaUrl || '/collections/all',
      current: d.current || ''
    };
  }

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Object.prototype.toString.call(list) === '[object Array]' ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode / quota: the strip simply stays empty */
    }
  }

  function currentHandle(cfg) {
    if (!cfg.current) return '';
    try {
      return JSON.parse(cfg.current).h || '';
    } catch (e) {
      return '';
    }
  }

  /* Remember the product we are standing on */
  function record(cfg) {
    if (!cfg.current) return;
    var item;
    try {
      item = JSON.parse(cfg.current);
    } catch (e) {
      return;
    }
    if (!item || !item.h) return;
    var list = read().filter(function (x) {
      return x && x.h && x.h !== item.h;
    });
    list.unshift(item);
    write(list.slice(0, MAX_STORED));
  }

  var ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return ESC[c];
    });
  }

  function cardHtml(item) {
    return (
      '<a class="rv__card" href="' +
      esc(item.u) +
      '"><span class="rv__media"><img src="' +
      esc(item.i) +
      '" alt="' +
      esc(item.t) +
      '" loading="lazy" width="400" height="400"></span><span class="rv__card-title">' +
      esc(item.t) +
      '</span><span class="rv__price">' +
      esc(item.p) +
      '</span></a>'
    );
  }

  function render() {
    var cfg = readCfg();
    if (!cfg) return;

    var all = read();
    var items = all.filter(function (x) {
      return x && x.h && x.h !== currentHandle(cfg);
    });

    /* Hiding the product we are standing on is nice, but if it would empty the
       strip the shopper reads that as "broken" — show the whole list instead. */
    if (!items.length && all.length) items = all;
    items = items.slice(0, cfg.limit);

    var roots = document.querySelectorAll('[data-rv-root]');
    Array.prototype.forEach.call(roots, function (root) {
      var titleEl = root.querySelector('[data-rv-title]');
      var scroller = root.querySelector('[data-rv-scroller]');
      var empty = root.querySelector('[data-rv-empty]');

      root.hidden = false;

      if (!items.length) {
        if (titleEl) {
          titleEl.hidden = true;
          titleEl.textContent = '';
        }
        scroller.hidden = true;
        scroller.innerHTML = '';
        empty.hidden = false;
        var welcome = empty.querySelector('[data-rv-welcome]');
        if (welcome) welcome.textContent = cfg.welcome;
        var cta = empty.querySelector('[data-rv-cta]');
        if (cta) {
          cta.textContent = cfg.ctaText;
          cta.setAttribute('href', cfg.ctaUrl);
        }
        return;
      }

      empty.hidden = true;
      if (titleEl) {
        titleEl.hidden = false;
        titleEl.textContent = cfg.title;
      }
      scroller.hidden = false;
      scroller.innerHTML = items.map(cardHtml).join('');
    });
  }

  /* Drop products that went offline / sold out since we cached them.

     ⚠️ Never use `/products.json?ids=` for this: on the storefront that
     ?ids= filter is silently ignored, so you get back an arbitrary page of
     unrelated products. Comparing handles against that list marks every
     remembered product as "gone" and wipes the whole strip. Ask for each
     product individually instead. */
  function validate() {
    var cfg = readCfg();
    if (!cfg) return;

    var now = Date.now();
    if (now - lastValidate < VALIDATE_TTL) return;
    lastValidate = now;

    var list = read().slice(0, cfg.limit || 6);
    if (!list.length) return;

    var root =
      window.Shopify && Shopify.routes && Shopify.routes.root
        ? Shopify.routes.root
        : '/';

    var checks = list.map(function (item) {
      return window
        .fetch(root + 'products/' + encodeURIComponent(item.h) + '.js', {
          credentials: 'same-origin'
        })
        .then(function (response) {
          /* 404 = unpublished, so the product really is gone */
          if (!response.ok) return { h: item.h, drop: true };
          return response.json().then(function (p) {
            var ok = p && p.available !== false;
            if (!ok && p && Array.isArray(p.variants)) {
              ok = p.variants.some(function (v) {
                return v.available;
              });
            }
            return { h: item.h, drop: !ok };
          });
        })
        .catch(function () {
          /* Network hiccup: no verdict, keep the snapshot */
          return null;
        });
    });

    if (!window.Promise) return;
    window.Promise.all(checks).then(function (results) {
      var drops = {};
      var gotVerdict = false;
      results.forEach(function (r) {
        if (!r) return;
        gotVerdict = true;
        if (r.drop) drops[r.h] = true;
      });
      /* Every single request failed: trust what we already have */
      if (!gotVerdict) return;

      var full = read();
      var next = full.filter(function (x) {
        return !drops[x.h];
      });
      if (next.length !== full.length) {
        write(next);
        render();
      }
    });
  }

  function refresh() {
    render();
    validate();
  }

  var cfg = readCfg();
  if (!cfg) return;

  record(cfg);
  render();

  /* Back/forward from the bfcache does not re-run this script */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    var again = readCfg();
    if (again) {
      record(again);
      render();
    }
  });

  /* The drawer is re-created by the theme (append-body clones the node), so
     listen in the capture phase instead of binding to the element itself. */
  document.addEventListener('mobile-nav:open', refresh, true);

  var drawer = document.getElementById('mobile-menu-drawer');
  if (drawer && window.MutationObserver) {
    new window.MutationObserver(function () {
      if (drawer.hasAttribute('open')) refresh();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }
})();
